import JraEncodeDecodeModule from './JraEncodeDecodeModule';

import { parents } from '../../../../util/dom';
import { leaveFloat } from '../../../../util/regex';
import { SECTION_EXPANDED_CLASS, OVERSEAS_CLASS } from '../constants';
import { getErrorMessage, resetInputValue, dispatchInputChangeEvent } from '../helpers';
import JraAddComponent from '../JraAddComponent';

export default class JraMembershipOptions extends JraEncodeDecodeModule {
  static EVENT_CHANGE = 'change';

  constructor(element, encodeDecodeKey) {
    super(encodeDecodeKey);
    this.element = element;
    this.textInputClass = 'textInput';
    this.postcodeInputId = 'addressPostCodeSearchID';

    this.initJraMembershipOptions();
  }


  encode(modules) {
    super.encode(modules);

    if (this.membershipPaymentTypeRadioButtons) {
      modules[this.key].activeMembershipPaymentButtonIndex
        = this.membershipPaymentTypeRadioButtons.findIndex((button) => {
          const radioControls = parents(button, '[data-jra-membership-radio-control]');
          const radioControl = radioControls.shift();
          return radioControl.classList.contains('active');
        });
    }

    let location = 'UK';
    if (this.form.classList.contains(OVERSEAS_CLASS)) {
      modules[this.key].isOverseas = true;
      location = 'Overseas';
    } else {
      modules[this.key].isOverseas = false;
    }

    if (this.membershipPaymentTypeHiddenInput) {
      modules[this.key].membershipPaymentTypeHiddenInput
        = this.membershipPaymentTypeHiddenInput.value;
    }

    const hiddenInput = this.element.querySelector(`[data-jra-membership-type-radio-group-hidden-input="${location}"]`);
    if (hiddenInput) {
      modules[this.key].subscriptionType = hiddenInput.value;
      modules[this.key].location = hiddenInput.dataset.jraMembershipTypeRadioGroupHiddenInput;
    }
  }

  decode() {
    super.decode();
    const module = this.getEncodedSessionData();
    if (!module) return;

    // 1: Detect and simulate change on overseas selection
    if (module.isOverseas) {
      const overSeasRadioButton = this.membershipTypeRadioButtons.find(b => b.value === 'Overseas');
      if (overSeasRadioButton) {
        dispatchInputChangeEvent(overSeasRadioButton);
      }
    } else {
      const ukRadioButton = this.membershipTypeRadioButtons.find(b => b.value === 'UK');
      if (ukRadioButton) {
        dispatchInputChangeEvent(ukRadioButton);
      }
    }


    // 2: Detect and simulate change on the combined subscriptionType and paymentType only on Renew
    if (
      module.activeMembershipPaymentButtonIndex !== undefined
      && module.activeMembershipPaymentButtonIndex > -1
      && this.membershipPaymentTypeRadioButtons
    ) {
      const button
        = this.membershipPaymentTypeRadioButtons[module.activeMembershipPaymentButtonIndex];
      if (button) {
        dispatchInputChangeEvent(button);
      }
    }

    if (module.subscriptionType) {
      // attempt to find the radio button only in the subscriptionType
      // variant that is in the correct location and has the correct type
      const subscriptionRadio = this.element.querySelector(
        `.JraMembershipBlock-subscriptionType [data-jra-membership-radio-list="${module.location}"] [data-jra-membership-subscription-type-radio-button="true"][value="${module.subscriptionType}"]`,
      );
      if (subscriptionRadio) {
        dispatchInputChangeEvent(subscriptionRadio);
      }
    }
  }

  initJraMembershipOptions() {
    this.form = document.querySelector('[data-form="form"]');
    this.membershipTypeRadioButtons = [...this.element.querySelectorAll('[data-jra-membership-type-radio-button]')]; // UK or Overseas
    this.membershipTypeUkButton = this.element.querySelector('[data-jra-membership-type-uk-button]');
    this.membershipTypeOverseasButton = this.element.querySelector('[data-jra-membership-type-overseas-button]');
    this.membershipPaymentTypeRadioButtons = [...this.element.querySelectorAll('[data-jra-membership-payment-type-radio-button]')];
    this.membershipPaymentTypeHiddenInput = this.element.querySelector('[data-jra-membership-payment-type]');
    this.membershipRadioLists = [...this.element.querySelectorAll('[data-jra-membership-radio-list]')];
    const validationElement = this.element.querySelector('[data-jra-membership-type-radio-group-hidden-input]');
    const validationMessage = getErrorMessage(validationElement, 'required');
    this.requiredValidationMessage = validationElement ?
      validationMessage : 'Please select a membership payment type!';
    this.addressInputs = [...this.form.querySelectorAll('[data-jra-form-address-input]')];

    this.toggleMembershipType = this.toggleMembershipType.bind(this);
    this.membershipTypeRadioButtons.forEach((button) => {
      button.addEventListener('change', this.toggleMembershipType);
    });

    this.membershipPaymentTypeRadioButtons.forEach((button) => {
      button.checked = false;
      button.addEventListener('change', () => this.setMembershipPaymentData(button));
    });
  }


  presetUK() {
    // variables for membership data, to be used in the payment summary section.
    this.membershipType = '';
    this.membershipPaymentType = '';
    this.membershipProduct = '';
    this.membershipPrice = '';
    this.membershipName = '';

    // set membership type to UK by default
    this.membershipTypeUkButton.checked = true;
    // we need to add active here so it validates correctly initially
    if (this.membershipTypeUkButton) {
      this.toggleMembershipType({
        currentTarget: this.membershipTypeUkButton,
      });
    }
  }


  presetOverseas() {
    // variables for membership data, to be used in the payment summary section.
    this.membershipType = '';
    this.membershipPaymentType = '';
    this.membershipProduct = '';
    this.membershipPrice = '';
    this.membershipName = '';

    // set membership type to overseas by default
    this.membershipTypeOverseasButton.checked = true;
    // we need to add active here so it validates correctly initially
    if (this.membershipTypeOverseasButton) {
      this.toggleMembershipType({
        currentTarget: this.membershipTypeOverseasButton,
      });
    }
  }


  toggleMembershipType(event) {
    const radioButton = event.currentTarget;
    const location = radioButton.value;
    // get hidden input relating to relevant radio list
    const hiddenInput = this.element.querySelector(`[data-jra-membership-type-radio-group-hidden-input="${location}"]`);
    hiddenInput.value = ''; // reset value so validation is reset
    this.membershipName = this.requiredValidationMessage;
    this.membershipPrice = 0;

    // the setting of the overseas modifier is used throughout the form.
    if (radioButton.value === 'Overseas' && radioButton.checked && !this.form.classList.contains(OVERSEAS_CLASS)) {
      this.form.classList.add(OVERSEAS_CLASS);
    } else if (radioButton.value !== 'Overseas' && radioButton.checked && this.form.classList.contains(OVERSEAS_CLASS)) {
      this.form.classList.remove(OVERSEAS_CLASS);
    }

    if (this.addressInputs.length > 0) {
      this.resetAddressFields();
    }

    // adding active has no style affect but serves to activate and deactivate validation
    this.membershipRadioLists.forEach((radioList) => {
      // set selected radioList to active
      if (radioList.dataset.jraMembershipRadioList === location) {
        radioList.classList.add(SECTION_EXPANDED_CLASS);

        const controls = [...radioList.querySelectorAll('[data-jra-membership-payment-type-radio-button]')];
        // reset all payment radio buttons
        controls.forEach((control) => {
          control.checked = false;
        });

        JraAddComponent.enableFormInputs(radioList);

        // pre select payment control if only one choice
        if (controls.length === 1) {
          const control = controls.pop();
          control.checked = true;
          this.setMembershipPaymentData(control);
        }
      } else {
        radioList.classList.remove(SECTION_EXPANDED_CLASS);
        JraAddComponent.disableFormInputs(radioList);
      }
    });

    if (this.membershipType && this.membershipType !== radioButton.value) {
      this.membershipType = radioButton.value;

      this.emit(JraMembershipOptions.EVENT_CHANGE, location, hiddenInput);
    }
  }


  setMembershipPaymentData = (input) => {
    const membershipPaymentType = input.dataset.jraMembershipPaymentType;
    const location = input.dataset.jraMembershipLocation;

    const hiddenInput = this.element.querySelector(`[data-jra-membership-type-radio-group-hidden-input="${location}"]`);
    hiddenInput.value = input.id;

    // set all others to not active and itself to active, this is for visual style on control
    this.membershipPaymentTypeRadioButtons.forEach((button) => {
      const radioControls = parents(button, '[data-jra-membership-radio-control]');
      const radioControl = radioControls.shift();
      radioControl.classList.remove('active');
      if (button.checked === true) {
        button.checked = false;
      }

      if (button === input) {
        radioControl.classList.add('active');
        button.checked = true;
      }
    });

    let paymentLogoImageSrc = '';
    let paymentLogoImageAlt = '';
    let paymentLogoClasses = 'JraSummary_paymentLogo';

    if (membershipPaymentType === 'Direct Debit') {
      paymentLogoImageSrc = '/dist/images/DirectDebit.svg';
      paymentLogoImageAlt = 'Direct Debit Logo';
    } else {
      paymentLogoImageSrc = '/dist/images/CreditCard.svg';
      paymentLogoImageAlt = 'Credit Card Logo';
      paymentLogoClasses = `${paymentLogoClasses} JraSummary_paymentLogo-card`;
    }

    this.membershipPaymentLogoString = `<img class="${paymentLogoClasses}" src="${paymentLogoImageSrc}" alt="${paymentLogoImageAlt}" />`;
    this.membershipPaymentTypeHiddenInput.value = membershipPaymentType;
    this.membershipPaymentType = membershipPaymentType;
    this.membershipPrice = leaveFloat(input.dataset.jraMembershipPrice);
    this.membershipProduct = input.value;
    this.membershipName = `(${input.dataset.jraMembershipProductDetail} by ${this.membershipPaymentType})`;

    this.emit(JraMembershipOptions.EVENT_CHANGE, location, hiddenInput);
  }


  resetAddressFields = () => {
    this.addressInputs.forEach((input) => {
      // if address lookup input, remove textInput class if present upon reset
      if (input.id === this.postcodeInputId) {
        const postcodeSearchControl = parents(input, '[data-jra-address-lookup="postcode-control"]')[0];
        if (postcodeSearchControl.classList.contains(this.textInputClass)) {
          postcodeSearchControl.classList.remove(this.textInputClass);
        }
      }
      resetInputValue(input);
    });
  }
}
